<template>
  <div class="modal-mask">
    <div class="modal-wrapper">
      <div
        class="modal-container"
        style="width: 80%;"
      >
        <!-- HEADER -->
        <div class="modal-header">
          <slot name="header">
            <h3 class="form-header">
              Message Info - {{ title }}
            </h3>
            <button
              class="red-close-button"
              @click="closeModal"
            >
              <i
                class="pi pi-times"
              ></i>
            </button>
          </slot>
        </div>
        <!-- BODY -->
        <div class="modal-body">
          <slot name="body">
            <div class="form-container">
              <!-- TABLE FOR MESSAGE HISTORY -->
              <DataTable
                v-model:expandedRows="expandedMessageBatchRows"
                :value="messageBatches"
                :paginator="true"
                :loading="loadingMessagingInfo"
                :total-records="messageBatchPageCount * messageBatchPerPage"
                :rows="messageBatchPerPage + 1"
                lazy
                data-key="batch_id"
                @page="loadPage"
              >
                <template #empty>
                  No records found
                </template>
                <template #loading>
                  Loading records, please wait...
                </template>
                <template #header>
                  <div class="flex-row">
                    <InputText
                      v-model="search_term"
                      placeholder="Search"
                      style="width: 30%;"
                      @keyup.enter="refresh"
                    />
                    <Calendar
                      v-model="startDate"
                      :show-icon="true"
                      :show-button-bar="false"
                      :show-week="false"
                      :show-time="false"
                      :placeholder="'Select Date'"
                      :date-format="dd/MM/yyyy"
                      @change="refresh"
                    />
                    <Calendar
                      v-model="endDate"
                      :show-icon="true"
                      :show-button-bar="false"
                      :show-week="false"
                      :show-time="false"
                      :placeholder="'Select Date'"
                      :date-format="dd/MM/yyyy"
                      @change="refresh"
                    />
                    <Button
                      icon="pi pi-refresh"
                      :loading="loadingMessagingInfo"
                      rounded
                      raised
                      style="margin-left: auto;"
                      @click="refresh"
                    />
                  </div>
                </template>
                <!-- EXPANDER -->
                <Column
                  expander
                  style="width: 5rem"
                />
                <!-- MESSAGE TIME -->
                <Column header="Message Time">
                  <template #body="slotProps">
                    {{ $d(new Date(slotProps.data.message_date), { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }) }}
                  </template>
                </Column>
                <!-- LOCATION NAME -->
                <Column
                  field="location_name"
                  header="Pharmacy"
                />
                <!-- STATUS -->
                <Column
                  field="status"
                  header="Status"
                >
                  <template #body="slotProps">
                    <span>
                      {{ slotProps.data.status === 'PENDING' ? 'SCHEDULED' : slotProps.data.status }}
                    </span>
                  </template>
                </Column>
                <!-- COMMMUNICATION TYPE -->
                <Column
                  field="communication_type"
                  header="Communication"
                />
                <!-- MESSAGE TYPE -->
                <Column
                  header="Type"
                >
                  <template #body="slotProps">
                    <span>
                      {{ getMessageType(slotProps.data) }}
                    </span>
                  </template>
                </Column> 
                <!-- MESSAGE COUNT -->
                <Column
                  field="num_patients"
                  header="# Patients"
                />
                <Column
                  field="cost"
                  header="Cost"
                >
                  <template #body="slotProps">
                    <span>
                      {{ $n(slotProps.data.cost,'currency') }}
                    </span>
                  </template>
                </Column>
                <!-- EXPANDABLE SECTION -->
                <template #expansion="slotProps">
                  <div>
                    <!-- MESSAGE DETAILS -->
                    <div
                      style="display: flex; flex-direction: row; gap: 20px; flex: 1;"
                    >
                      <div 
                        class="form-container"
                      >
                        <div 
                          class="form-row"
                        >
                          <strong> {{ getMessageType(slotProps.data) }} Name:</strong>
                          <span>
                            {{ getMessageName(slotProps.data) }}
                          </span>
                        </div>
                        <div class="form-row">
                          <strong># Fake Patients: </strong>
                          <span>
                            {{ slotProps.data.fake_patients }}
                          </span>
                        </div>
                        <div class="form-row">
                          <strong>Created By: </strong>
                          <span>
                            {{ slotProps.data.user_name }}
                          </span>
                        </div>
                        <div class="form-row">
                          <strong># Delivered: </strong>
                          <span>
                            {{ slotProps.data.num_delivered }}
                          </span>
                          <strong># Failed: </strong>
                          <span>
                            {{ slotProps.data.num_failed }}
                          </span>
                          <strong># Skipped: </strong>
                          <span>
                            {{ slotProps.data.num_skipped }}
                          </span>
                        </div>
                      </div>
                      <div 
                        class="form-container"
                      >
                        <div 
                          class="form-row"
                        >
                          <strong>Context:</strong>
                          <span>
                            {{ slotProps.data.message_context }}
                          </span>
                        </div>
                      
                        <div 
                          v-if="slotProps.data.template_name"
                          class="form-row"
                        >
                          <strong>Template:</strong>
                          <span>
                            {{ slotProps.data.template_name }}
                          </span>
                        </div>
                        <div 
                          v-if="slotProps.data.subject"
                          class="form-row"
                        >
                          <strong>Subject:</strong>
                          <span>
                            {{ slotProps.data.subject }}
                          </span>
                        </div>
                        <div class="form-row">
                          <strong>Message: </strong>
                          <span
                            v-if="slotProps.data.body"
                          >
                            {{ slotProps.data.body }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </DataTable>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
  <ConfirmDialog group="location_etl_confirmation" />
</template>
  
<script>
import axios from 'axios';

export default {
    name: 'AdminMessagingInfoModal',
    props: {
        /**
         * The location_id of the pharmacy
         * @type {String}
         */
        locationId: {
          type: String,
          default: null
        },
        /**
         * The location_name of the pharmacy
         * @type {String}
         */
        locationName: {
          type: String,
          default: null
        },
        /**
         * The campaign_id 
         * @type {String}
         */
        campaignId: {
          type: String,
          default: null
        },
        /**
         * The campaign_name
         * @type {String}
         */
        campaignName: {
          type: String,
          default: null
        },
        /**
         * The trial_id
         * @type {String}
         */
        trialId: {
          type: String,
          default: null
        },
        /**
         * The trial_name
         * @type {String}
         */
        trialName: {
          type: String,
          default: null
        },
         /**
         * The sequence_id
         * @type {String}
         */
        sequenceId: {
          type: String,
          default: null
        },
        /**
         * The sequence_name
         * @type {String}
         */
        sequenceTitle: {
          type: String,
          default: null
        },
        /**
         * The follow_up_id
         * @type {String}
         */
        followUpId: {
          type: String,
          default: null
        },
    },
    /**
     * Emits events to parent component
     */
    emits: [
        'closeModal'
    ],
    data() {
        /**
         * The local data for the component
         */
        return {
          search_term: '',
          expandedMessageBatchRows: [],
          messageBatches: [],
          messageBatchCurrentPage: 1,
          messageBatchPerPage: 20,
          messageBatchPageCount: 1,
          loadingMessagingInfo: false,
          messagingInfoError: '',
          startDate: null,
          endDate: null,
        };
    },
    computed: {
      /**
       * The title of the modal
       */
      title() {
        if (this.campaignName) {
          return 'Campaign: ' + this.campaignName;
        }
        else if (this.trialName) {
          return 'Trial: ' + this.trialName;
        }
        else if (this.sequenceTitle) {
          return 'Sequence: ' + this.sequenceTitle;
        }
        else if (this.locationName) {
          return this.locationName;
        }
        else {
          return '';
        }
      },
      /**
       * The payload for the messaging info request
       */
      messagingInfoPayload() {
        return {
          search_term: this.search_term == '' ? null : this.search_term,
          start_date: this.startDate,
          end_date: this.endDate,
          location_id: this.locationId,
          campaign_id: this.campaignId,
          trial_id: this.trialId,
          sequence_id: this.sequenceId,
          follow_up_id: this.followUpId,
          page: this.messageBatchCurrentPage,
        }
      },
    },
    mounted() {
      this.refresh();
    },
    methods: {
        /**
         * Emit the close modal event
         */
        closeModal() {
            this.$emit('closeModal');
        },
        async refresh() {
          this.$store.dispatch('getRefreshToken');
          this.loadingMessagingInfo = true;
          this.messagingInfoError = '';
          if (this.messageBatchCurrentPage === 1) {
            this.messageBatches = [];
            this.expandedMessageBatchRows = [];
          }

          await axios.post('/api/admin/messaging-info', this.messagingInfoPayload, this.$store.state.header)
          .then(res => {
            this.handleRefreshSuccess(res);
          })
          .catch(err => {
            this.handleRefreshError(this.handleAxiosError(err));
          })
        },
        /**
         * Handle the refresh success case
         * @param {Object} res
         */
        handleRefreshSuccess(res) {
          console.log('The refresh response is', res.data);
          this.loadingMessagingInfo = false;
          this.messageBatches = res.data.message_batches;
          this.messageBatchCurrentPage = res.data.current_page
          this.messageBatchPerPage = res.data.per_page
          this.messageBatchPageCount = res.data.page_count

          // Close all expanded rows
          this.messageBatches.forEach(batch => {
        this.expandedMessageBatchRows.push({
          [batch.batch_id]: false
        })
      })

        },
        /**
         * Handle the refresh error case
         * @param {Object} err
         */
        handleRefreshError(err) {
          console.log('The refresh queue error is', err);
          this.loadingMessagingInfo = false;
          this.messagingInfoError = err;
        },
        /**
         * Get the message type
         * @param {Object} messageBatch
         */
        getMessageType(messageBatch) {
          if (messageBatch.campaign_id) {
            return 'Campaign';
          }
          else if (messageBatch.trial_id) {
            return 'Trial';
          }
          else if (messageBatch.sequence_id) {
            return 'Sequence';
          }
          else {
            return 'Communication';
          }
        },
        /**
         * Get the message name
         * @param {Object} messageBatch
         */
        getMessageName(messageBatch) {
          if (messageBatch.campaign_id) {
            return messageBatch.campaign_name;
          }
          else if (messageBatch.trial_id) {
            return messageBatch.trial_name;
          }
          else if (messageBatch.sequence_id) {
            if (messageBatch.follow_up_id) {
              return  messageBatch.sequence_title + ' - (Message ' + messageBatch.follow_up_order + ')';
            }

            return messageBatch.sequence_title;
          }
          else {
            return '-';
          }
        },
        /**
         * load the page
         * @param {Object} event
         */
        loadPage(event) {
          this.messageBatchCurrentPage = event.page + 1;
          this.refresh();
        },
        /**
         * Get the upload severity
         * @param {String} status 
         */
        getUploadSeverity(status) {
          switch (status) {
            case 'RECEIVED':
            case 'OVERRIDE':
            case 'REQUESTED':
              return 'info';
            case 'NOT RECEIVED':
              return 'warning';
            case 'PROCESSING':
              return 'help';
            case 'ERROR':
              return 'danger';
            case 'COMPLETED':
              return 'success';
            default:
              return 'secondary';
          }
        },
    }
}
</script>
  
<style lang="scss" scoped>
@import "../../assets/css/main.scss";

.advanced-options {
  border: 1px dashed grey;
  padding: 10px;
}

.modal-container {
  display: flex;
  flex-direction: column;
  height: 78vh;
  width: 80%;
}

.error-message {
  color: $error;
}

.success-message {
  color: $correct;
}

.form-container {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: stretch;

  .span {
    display: flex;
  }
}

.flex-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
  text-align: left;
}

.flex-input > label {
  font-size: 0.8rem;
}

.p-inputtext,
.p-selectbutton,
.p-password-input,
.p-togglebutton {
  border-radius: 5px;
  border: 1px solid $phoneorders;
  overflow: hidden;
}

.p-selectbutton .p-button .p-password-input {
  border: none !important;
}

:deep(.p-selectbutton) {
  display: flex;
}

:deep(.p-selectbutton) div {
  flex: 1;
}

.flex-item {
  flex: 1;
}

.flex-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
}

.flex-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-body {
  display: flex;
  flex-direction: column;
  padding: 20px;
  flex: 1;
}

</style>
